import WithUserTimeHOC, { WithUserTimeProps } from '@hocs/WithUserTime'
import { SessionInfoList as SessionInfoListInterface } from '@interfaces/LessonGroups'
import { Material as MaterialInterface, MaterialWithUnits } from '@interfaces/Material'
import { GroupMaterial } from '@interfaces/MySchedule'
import { GET_LEARNING_PATH, GET_MATERIAL } from '@queries/index'
import { getUnitPrevIndex } from '@utils/helpers'
import { LP_UNIT_TYPE } from '@utils/constants/learningPath'
import orderBy from 'lodash/orderBy'
import find from 'lodash/find'
import get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import map from 'lodash/map'
import React from 'react'
import { withApollo, WithApolloClient } from 'react-apollo'
import { PreviousMaterialError } from './Errors'
import { RegistrationMaterial, RegistrationWrapper } from '@interfaces/Student'
import { getRegFromUsers } from '@utils/helpers/lessons'
import { GET_DATA } from '@components/Modal/SingleProfileModal/QuerySingleProfile'
import { TLessonHistory } from '@interfaces/MyLesson'
import { GET_ALL_LEARNING_UNITS, GET_LEARNING_PATH_BY_LPIDS_ALIAS } from '@queries/learningPath'
import getSuggestedMaterial from './utils'
import { GET_TOPIC_GROUP } from '@queries/topicGroup'
import { GetTopicGroupPayload, ITopicGroup } from '@interfaces/TopicGroup'
import { GetAllLearningUnitsPayload, GetAllLearningUnitsResponse } from '@interfaces/LearningPath'

interface QueryPageResults {
  loading: boolean
  error: boolean
  errorMessage: string
  refetch: Function
  LastMaterialList: MaterialInterface[]
  MaterialList: MaterialInterface[]
  MaterialOptions: MaterialWithUnits[]
  GroupMaterials: GroupMaterial[]
  activeMaterial: string
}

interface QueryPageDataProp {
  LastMaterialList: MaterialInterface[]
  MaterialList: MaterialInterface[]
  SessionInfoList: SessionInfoListInterface[]
  GroupMaterials?: GroupMaterial[]
  IdType?: string
  fetchMaterials?: boolean
  isFlex?: boolean
  isBoD1?: boolean
  ProgramId?: string
  LessonHistory?: TLessonHistory
  children: (results: QueryPageResults) => React.ReactNode
}

export type Props = WithApolloClient<QueryPageDataProp & WithUserTimeProps>

class QueryPageData extends React.Component<Props> {
  _isMounted = false
  materialAbortCtrl = new AbortController()
  prevMaterialAbortCtrl = new AbortController()
  datesAbortCtrl = new AbortController()
  learningPathAbortCtrl = new AbortController()
  topicAbortCtrl = new AbortController()
  allUnitAbortCtrl = new AbortController()

  state = {
    loading: false,
    error: false,
    errorMessage: '',
    activeMaterial: '',
    LastMaterialList: [] as MaterialInterface[],
    MaterialList: [] as MaterialInterface[],
    MaterialOptions: [] as MaterialWithUnits[],
    GroupMaterials: [] as GroupMaterial[],
  }

  async componentDidMount() {
    const { isBoD1, SessionInfoList } = this.props
    this._isMounted = true
    if (isBoD1 && SessionInfoList?.[0]?.StudentNote) {
      this.fetchTopicData()
    } else {
      this.fetchData()
    }
  }

  async componentDidUpdate(prevProps) {
    const { LastMaterialList, MaterialList, isBoD1, SessionInfoList } = this.props
    if (
      (this._isMounted && !isEqual(prevProps.LastMaterialList, LastMaterialList)) ||
      !isEqual(prevProps.MaterialList, MaterialList)
    ) {
      if (isBoD1 && SessionInfoList?.[0]?.StudentNote) {
        this.fetchTopicData()
      } else {
        this.fetchData()
      }
    }
  }

  componentWillUnmount() {
    this._isMounted = false
    this.materialAbortCtrl.abort()
    this.learningPathAbortCtrl.abort()
  }

  fetchData = async (LPID?: string) => {
    const { LastMaterialList, MaterialList, SessionInfoList, fetchMaterials, GroupMaterials, ProgramId, isBoD1 } =
      this.props
    try {
      this.setState({
        loading: true,
        error: false,
      })

      // const LastMaterial = await this.getMaterial(LastMaterialList)
      // const Material = await this.getMaterial(MaterialList)

      const LastMaterial = LastMaterialList
      const Material = MaterialList

      let MaterialOptions = [] as MaterialWithUnits[]

      const suggested = getSuggestedMaterial(LastMaterial, Material, MaterialOptions, false)
      let lpid = LPID ?? suggested?.Material

      if (isBoD1) {
        lpid = SessionInfoList[0]?.LPId
      }

      if (fetchMaterials) {
        const registrations = await this.getUsersRegistrations(SessionInfoList)
        const RegMats = registrations
          .filter((reg) => reg.RegistrationInfo?.Programs?.map((prog) => prog?.ProgramId).indexOf(ProgramId || '') >= 0)
          .reduce((mats: RegistrationMaterial[], reg: RegistrationWrapper) => {
            return mats.concat(reg?.RegistrationInfo?.Materials)
          }, [])
          .filter(({ IndustrySpecificContent }) => !IndustrySpecificContent) // PS-2683: Exclude Industry specific content
        let matOptions: any = []
        try {
          //matOptions = (await this.getLearningPath(SessionInfoList, RegMats)) as never[][]
          matOptions = await this.getLearningPath2(SessionInfoList, RegMats, lpid)
        } catch (e) {
          matOptions = []
        }
        const matsFromReg = RegMats?.map(({ LPID }) => LPID)
        const uniqMaterials = [].concat
          .apply([], matOptions)
          .reduce((arr: MaterialWithUnits[], c: MaterialWithUnits) => {
            const exitingMats = arr?.map((m) => m?.Id)
            if (exitingMats.indexOf(c?.Id) < 0) {
              arr.push(c)
            }
            return arr
          }, [])

        MaterialOptions = uniqMaterials?.filter((mat) => matsFromReg?.indexOf(mat?.Id) >= 0)

        if (isBoD1) {
          MaterialOptions = uniqMaterials
        }

        // Lets override LastMaterial's MaterialName here and used MaterialName from registration
        const displayname = RegMats.find(({ LPID }) => LPID === LastMaterial?.[0]?.Material)?.MaterialName
        if (LastMaterial[0]) {
          LastMaterial[0].MaterialName = displayname || LastMaterial?.[0]?.MaterialName
        }
      }

      let groupMaterials = [] as GroupMaterial[]
      if (GroupMaterials) {
        /**
         * There maybe an instance wherein Material is not empty,
         * however Material field is null. Instructor might set student(s)
         * to absent and immediately hit submit.
         *
         * Let's throw an informative error on this scenario
         */
        const isAllAbsent = SessionInfoList?.every((session) => session?.Attendance === 'Absent')
        const isAllEmpty = GroupMaterials?.every((gm) => this.isEmptyMaterials(gm?.MaterialList)) ?? false
        if (isAllAbsent && isAllEmpty) {
          throw new PreviousMaterialError(
            'Student(s) have been marked as absent and no previous materials are available'
          )
        }

        const promisedGroupMaterials = map(GroupMaterials, async (gm) => {
          const MaterialList = await this.getMaterial(gm?.MaterialList)
          const LastMaterialList = await this.getMaterial(gm?.LastMaterialList)

          return {
            ...gm,
            MaterialList,
            LastMaterialList,
          }
        })
        groupMaterials = await Promise.all(promisedGroupMaterials)
      }

      if (this._isMounted) {
        this.setState({
          loading: false,
          LastMaterialList: LastMaterial,
          MaterialList: Material,
          MaterialOptions,
          GroupMaterials: groupMaterials,
          /**
           * an active material clicked by the user
           */
          activeMaterial: lpid,
        })
      }
    } catch (error) {
      if (this._isMounted) {
        this.setError(error as Error)
      }
    }
  }

  fetchTopicData = async () => {
    const { LastMaterialList, MaterialList, ProgramId, isBoD1, client } = this.props
    try {
      this.setState({
        loading: true,
        error: false,
      })

      const LastMaterial = LastMaterialList
      const Material = MaterialList

      let MaterialOptions = [] as MaterialWithUnits[]

      const topicRes = await client.query<ITopicGroup, GetTopicGroupPayload>({
        query: GET_TOPIC_GROUP,
        variables: {
          ProgramId: ProgramId!,
        },
        context: {
          fetchOptions: {
            signal: this.topicAbortCtrl.signal,
          },
        },
      })

      const allUnitRes = await client.query<GetAllLearningUnitsResponse, GetAllLearningUnitsPayload>({
        query: GET_ALL_LEARNING_UNITS,
        variables: {
          Id: topicRes?.data?.getTopicGroup?.TopicLPID ?? '',
          StudentId: '',
        },
        context: {
          fetchOptions: {
            signal: this.allUnitAbortCtrl.signal,
          },
        },
      })

      let matOptions: MaterialWithUnits[][] = []
      try {
        let d: any = []
        if (d.length === 0 && isBoD1) {
          const lp = topicRes?.data?.getTopicGroup
          const units = allUnitRes?.data?.getAllLearningUnits
          d.push({
            lpid: lp?.TopicLPID,
            details: {
              units: units || [],
            },
            shortname: 'Own Material',
          })
        }

        if (d.length > 0) {
          const lp = d.map((learning) => {
            let units = get(learning.details, 'units', [])
            units = units.filter((u) => u.isunit0)

            units = map(units, (val) => {
              /**
               * https://berlitz.atlassian.net/wiki/spaces/B2/pages/3174957080/Instructor+Guides+-+Open+materials
               *
               * Private Student and Groups - Live Online (all type of programs)
               * ig - Instructor Guide HTML guides
               */
              let url = get(find(val.attachments, { type: 'course_ig_pdf' }), 'url')

              return {
                Id: val.unitid,
                Name: val.fullname,
                URL: url,
                UnitType: val.unittype,
              }
            })

            return {
              Chapter: '',
              Id: `${learning.lpid}`,
              Name: learning.shortname || get(learning.details, 'fullname.0.fullname'),
              Page: '',
              Point: '',
              Type: 'LearningPath',
              Units: units,
              IsTest: false,
            }
          })
          matOptions = matOptions.concat(lp)
        }
      } catch (e) {
        matOptions = []
      }

      MaterialOptions = ([] as MaterialWithUnits[]).concat
        .apply([] as MaterialWithUnits[], matOptions)
        .reduce((arr: MaterialWithUnits[], c: MaterialWithUnits) => {
          const exitingMats = arr?.map((m) => m?.Id)
          if (exitingMats.indexOf(c?.Id) < 0) {
            arr.push(c)
          }
          return arr
        }, [])

      let groupMaterials = [] as GroupMaterial[]
      if (this._isMounted) {
        this.setState({
          loading: false,
          LastMaterialList: LastMaterial,
          MaterialList: Material,
          MaterialOptions,
          GroupMaterials: groupMaterials,
          /**
           * an active material clicked by the user
           */
          activeMaterial: topicRes?.data?.getTopicGroup?.TopicLPID,
        })
      }
    } catch (error) {
      if (this._isMounted) {
        this.setError(error as Error)
      }
    }
  }

  getMaterial = async (Materials: MaterialInterface[]): Promise<MaterialInterface[]> => {
    const { client } = this.props
    const promisedMaterials = map(Materials, async (material) => {
      if (material.Material === 'Own Material') {
        return {
          ...material,
          Name: material.Material,
          ChapterName: material.Chapter,
        }
      }

      const materialInfo = await client.query({
        query: GET_MATERIAL,
        variables: { Id: material.Material },
        context: {
          fetchOptions: {
            signal: this.materialAbortCtrl.signal,
          },
        },
      })

      const units = get(materialInfo.data, 'getLearningPathById.units')
      const unit = find(units, { unitid: parseInt(material.Chapter, 10) })
      return {
        ...material,
        Name: get(
          materialInfo.data,
          'getLearningPathById.shortname',
          get(materialInfo.data, 'getLearningPathById.fullname.0.fullname', material.Material)
        ),
        ChapterName: get(unit, 'fullname'),
      }
    })

    return Promise.all(promisedMaterials)
  }

  getLearningPath2 = async (
    SessionInfoList: SessionInfoListInterface[],
    regMaterials: RegistrationMaterial[],
    LPID?: string
  ): Promise<MaterialWithUnits[][]> => {
    const { client, profile, isFlex, isBoD1 } = this.props
    const promisedMaterialOptions = map(SessionInfoList, async (session) => {
      let options: MaterialWithUnits[] = []

      if (!session.CustomerId) {
        return options
      }

      const res = await client.query({
        query: GET_LEARNING_PATH_BY_LPIDS_ALIAS,
        variables: {
          StudentId: session.CustomerId,
          IdType: profile.Type,
          Ids: LPID || regMaterials[0]?.LPID || '',
          berlitzEnv: session.MyBerlitzEnvironment,
          isTopic: isBoD1,
        },
        context: {
          fetchOptions: {
            signal: this.learningPathAbortCtrl.signal,
          },
        },
      })

      const data: any = get(res, 'data')

      const d = regMaterials
        .map((r) => {
          return {
            lpid: r.LPID,
            details: {
              units: [] as any,
            },
            shortname: r.MaterialName,
          }
        })
        .reduce((acc, val) => {
          if (!data || !data.getLearningPath) {
            acc.push(val)
            return acc
          }

          const rm = data.getLearningPath.find((lp) => lp.lpid === val.lpid)

          if (rm) {
            const newVal = {
              ...val,
              details: rm?.details,
            }
            acc.push(newVal)
          } else {
            acc.push(val)
          }

          return acc
        }, [] as any)

      if (d.length === 0 && isBoD1) {
        const lp = res?.data?.getLearningPath[0]
        d.push({
          lpid: LPID,
          details: {
            units: lp?.details?.units || [],
          },
          shortname: lp?.shortname,
        })
      }

      if (d.length > 0) {
        const lp = d.map((learning) => {
          let units = get(learning.details, 'units', [])
          const unit = units.find((val) => val.unittype === LP_UNIT_TYPE.MATERIAL)
          if (unit) {
            units = units.filter((u) => u.unitid !== get(unit, 'unitid'))
          }

          if (isFlex) {
            units = units.filter((unit) => unit['unittype'] === LP_UNIT_TYPE.COACHING)
          } else {
            units = units.filter((unit) => ![LP_UNIT_TYPE.COACHING].includes(unit['unittype']))
          }

          units = map(units, (val) => {
            /**
             * https://berlitz.atlassian.net/wiki/spaces/B2/pages/3174957080/Instructor+Guides+-+Open+materials
             *
             * Private Student and Groups - Live Online (all type of programs)
             * ig - Instructor Guide HTML guides
             */
            let url =
              get(find(val.activities, { type: 'ig' }), 'url') || get(find(val?.attachments, { type: 'ig_pdf' }), 'url')

            /**
             * Berlitz Flex
             * p5_instructor - Instructor Guide HTML Berlitz Flex guides
             */
            if (val.unittype === LP_UNIT_TYPE.COACHING) {
              url = get(find(val?.activities, { type: 'p5_instructor' }), 'url')
            }

            return {
              Id: val.unitid,
              Name: val.fullname,
              URL: url,
              UnitType: val.unittype,
            }
          })

          return {
            Chapter: '',
            Id: `${learning.lpid}`,
            Name: learning.shortname || get(learning.details, 'fullname.0.fullname'),
            Page: '',
            Point: '',
            Type: 'LearningPath',
            Units: units,
            IsTest: regMaterials.find(({ LPID }) => LPID === learning.lpid)?.IsTest ?? false,
          }
        })
        options = options.concat(lp)
      }
      return options
    })

    return Promise.all(promisedMaterialOptions)
  }

  isEmptyMaterials = (materials: MaterialInterface[] = []): boolean => {
    return materials.every((material) => !material.Material)
  }

  setError = (error: Error) => {
    if (error.name === 'PreviousMaterialError') {
      this.setState({
        loading: false,
        error: true,
        errorMessage: error.message,
      })
    } else {
      this.setState({
        loading: false,
        error: true,
      })
    }
  }

  getUsersRegistrations = async (sessions: SessionInfoListInterface[]) => {
    const { client } = this.props
    let studentIds = sessions.map((s) => s.CustomerId)

    const requests = studentIds.map((StudentId) => {
      const params = {
        StudentId,
        Language: 'English',
      }

      return client.query({
        query: GET_DATA,
        variables: params,
      })
    })
    const responses = await Promise.all(requests)
    let studentProfiles = responses.map((i) => i.data?.getStudents?.[0]?.StudentProfileInfo)
    let registrations = getRegFromUsers(studentProfiles)
    this.setState({ registrations })
    return registrations
  }

  render() {
    const { children } = this.props
    const {
      loading,
      error,
      errorMessage,
      LastMaterialList,
      MaterialList,
      MaterialOptions,
      GroupMaterials,
      activeMaterial,
    } = this.state

    return children({
      loading,
      error,
      errorMessage,
      LastMaterialList,
      MaterialList,
      MaterialOptions,
      GroupMaterials,
      activeMaterial,
      refetch: this.fetchData,
    })
  }
}

export default WithUserTimeHOC(withApollo(QueryPageData))
